// 스테이지 세트피스 로더(S2 지역1·S6 지역2): public/assets/r1/set_<이름>.glb(tools/blender/r1_stage_sets.py),
// 지역2는 public/assets/r2/set_<이름>.glb(tools/blender/r2_stage_sets.py, 이름이 e_ 로 시작). 경로는 stageSets.setFile.
// 지역마다 1K 아틀라스 한 장(<지역>/tex/sets_atlas.webp, 발광 sets_atlas_emit.webp)을 쓴다 — GLB에는 위치·법선·UV만 있다.
// 발이 원점, 정면 +Z. 파일이 없거나 깨졌으면 null(그 조각만 빠진다). 맵 사이에서 한 번만 읽는다.
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { float, frontFacing, mix, positionWorld, select, smoothstep, texture, uv, vec3 } from 'three/tsl';
import { assetUrl, loadTexture } from './kits';
import { ditherKeep, kitCutNodes } from './cut';

export type { SetName } from './stageSets';
import { setFile, type SetName } from './stageSets';

export interface SetAsset {
  readonly name: SetName;
  readonly geometry: THREE.BufferGeometry;
  /** bbox 치수(m) — 폭 x, 높이 y, 깊이 z */
  readonly w: number;
  readonly h: number;
  readonly d: number;
  readonly triangles: number;
}

let loader: GLTFLoader | null = null;
const cache = new Map<SetName, Promise<SetAsset | null>>();

function toFloat(g: THREE.BufferGeometry, name: string): void {
  const a = g.getAttribute(name) as THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined;
  if (!a) return;
  const inter = (a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute === true;
  if (!inter && a.array instanceof Float32Array && !a.normalized) return;
  const s = a.itemSize, n = a.count;
  const out = new Float32Array(n * s);
  for (let i = 0; i < n; i++) {
    out[i * s] = a.getX(i);
    if (s > 1) out[i * s + 1] = a.getY(i);
    if (s > 2) out[i * s + 2] = a.getZ(i);
  }
  g.setAttribute(name, new THREE.BufferAttribute(out, s));
}

async function loadFile(name: SetName): Promise<SetAsset | null> {
  try {
    loader ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    const gltf = await loader.loadAsync(assetUrl(setFile(name)));
    let mesh: THREE.Mesh | null = null;
    gltf.scene.traverse((o) => { if (!mesh && (o as THREE.Mesh).isMesh) mesh = o as THREE.Mesh; });
    if (!mesh) throw new Error('메시 없음');
    const src = mesh as THREE.Mesh;
    src.updateWorldMatrix(true, false);
    const geometry = src.geometry.clone();
    for (const key of Object.keys(geometry.attributes)) if (key !== 'position' && key !== 'normal' && key !== 'uv') geometry.deleteAttribute(key);
    toFloat(geometry, 'position'); toFloat(geometry, 'normal'); toFloat(geometry, 'uv');
    geometry.applyMatrix4(src.matrixWorld);
    if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    const triangles = geometry.index ? geometry.index.count / 3 : geometry.getAttribute('position').count / 3;
    const mat = src.material as THREE.Material | undefined;
    mat?.dispose();
    src.geometry.dispose();
    return { name, geometry, w: bb.max.x - bb.min.x, h: bb.max.y - bb.min.y, d: bb.max.z - bb.min.z, triangles };
  } catch (err) {
    console.info('세트피스를 읽지 못했다(그 조각은 빠진다): ' + name, err instanceof Error ? err.message : err);
    return null;
  }
}

/** 세트피스 하나(맵 사이 공유) */
export function loadSet(name: SetName): Promise<SetAsset | null> {
  let p = cache.get(name);
  if (!p) cache.set(name, (p = loadFile(name)));
  return p;
}

export interface SetAtlas {
  readonly map: THREE.Texture | null;
  readonly emissive: THREE.Texture | null;
}
const atlasP = new Map<'r1' | 'r2', Promise<SetAtlas>>();
/** 지역 공용 아틀라스(밝기 + 발광). 못 읽으면 둘 다 null(재질이 단색으로 대신한다) */
export function loadSetAtlas(region: 'r1' | 'r2' = 'r1'): Promise<SetAtlas> {
  let p = atlasP.get(region);
  if (p) return p;
  p = Promise.all([loadTexture(region + '/tex/sets_atlas.webp'), loadTexture(region + '/tex/sets_atlas_emit.webp')]).then(([map, emissive]) => {
    // 아틀라스는 반복하지 않는다(타일 경계 번짐 방지: UV 여백이 이미 들어 있다)
    // GLB의 UV는 glTF 규약(v 아래로)이라 TextureLoader 기본 flipY를 끈다
    for (const t of [map, emissive]) if (t) { t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.anisotropy = 4; t.flipY = false; t.needsUpdate = true; }
    return { map, emissive };
  });
  atlasP.set(region, p);
  return p;
}

export interface SetMaterialOptions {
  /** 카메라 쪽이면 낮게 잘라내고 뒷면을 단면색으로 칠한다(목책·난간·가장자리 돌) */
  readonly clip: boolean;
  /** 컷 단면 색 */
  readonly cap: readonly [number, number, number];
  /** 발광 아틀라스를 쓰는 조각(모닥불·룬·횃불) */
  readonly emissive: boolean;
  /** 발광 세기(기본 1.5). 지역2 용암·균열은 더 세게 */
  readonly glow?: number;
}

/** 세트피스 재질: 아틀라스 색(× 인스턴스 색은 three가 곱한다), 카메라 쪽 컷(clip) 또는 디더, 발광 아틀라스 */
export function setMaterial(atlas: SetAtlas, opt: SetMaterialOptions): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.93, metalness: 0, side: opt.clip ? THREE.DoubleSide : THREE.FrontSide });
  const cut = kitCutNodes();
  const dither = ditherKeep(cut.dither as THREE.Node<'float'>);
  const base = atlas.map ? texture(atlas.map, uv()).rgb : vec3(0.58, 0.55, 0.48);
  // 인스턴스 색(setColorAt)은 노드 재질이 알아서 곱한다
  const shaded = base;
  if (opt.clip) {
    m.maskNode = cut.keep.and(dither);
    const cap = vec3(opt.cap[0], opt.cap[1], opt.cap[2]);
    m.colorNode = select(frontFacing, shaded, cap.mul(mix(float(0.7), float(1.0), smoothstep(-0.5, 0.6, positionWorld.y))));
  } else {
    m.maskNode = dither;
    m.colorNode = shaded;
  }
  if (opt.emissive && atlas.emissive) m.emissiveNode = texture(atlas.emissive, uv()).rgb.mul(select(frontFacing, float(opt.glow ?? 1.5), float(0)));
  return m;
}
